import { FC, UIEvent, useEffect, useRef } from 'react';
import styled from 'styled-components';

import { colors, spacing } from 'ui/theme';

import { useAssetsQueryContext } from '../../contexts/AssetsQueryContext';
import { usePanelContext } from '../../contexts/Panel';
import { usePaginationContext } from 'utils/contexts/pagination';
import { PanelView } from '../../types';

const ScrollableContainer = styled.div`
  border-color: ${colors.grey15};
  border-radius: ${spacing.quarter};
  border-style: solid;
  border-width: ${spacing.single};
  bottom: 0;
  display: flex;
  flex-direction: column;
  left: 0;
  overflow: auto;
  position: absolute;
  right: 0;
  top: 0;
`;

export const testIds = {
  container: 'cl-scrollable-list-container',
};

// a scrollable container that remembers its scroll position
export const ScrollableListContainer: FC<React.PropsWithChildren<unknown>> = ({ children }) => {
  const { currentPage, setCurrentPage } = usePaginationContext();
  const { assetCount, assetCountTotal, isLoading: isFetchingAssets } = useAssetsQueryContext();
  const { panelView, scrollBookmark, setScrollBookmark } = usePanelContext();
  const scrollableArea = useRef<HTMLDivElement>(null);
  const scrollPosition = useRef<number>(0);

  const handleScroll = (e: UIEvent<HTMLDivElement>) => {
    const eventTarget = e.target as HTMLDivElement;
    const scrollableHeight = eventTarget.scrollHeight - eventTarget.clientHeight;
    scrollPosition.current = eventTarget.scrollTop;

    if (
      eventTarget.scrollTop >= scrollableHeight - 20 &&
      !isFetchingAssets &&
      assetCount < assetCountTotal
    ) {
      // within 20 px of the bottom
      setCurrentPage(currentPage + 1);
    }
  };

  useEffect(() => {
    const scrollArea = scrollableArea.current;
    if (scrollBookmark && panelView === PanelView.List && scrollArea) {
      scrollArea.scrollTo(0, scrollBookmark);
    }
    return () => {
      if (panelView === PanelView.List && scrollPosition.current) {
        setScrollBookmark(scrollPosition.current);
      }
    };
  }, []);

  return (
    <ScrollableContainer data-test={testIds.container} onScroll={handleScroll} ref={scrollableArea}>
      {children}
    </ScrollableContainer>
  );
};
